import { supabase } from "./supabase";

// Cache implementation pattern following the project guidelines
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const getCachedData = (key) => {
  const cached = cache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log(`📋 Education Cache hit for ${key}`);
    return cached.data;
  }
  return null;
};

const setCachedData = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
  console.log(`💾 Cached education data for ${key}`);
};

// Cache management
export const clearEducationCache = () => {
  cache.clear();
  console.log("🧹 Education cache cleared");
};

// Get published education content for affiliates
export const getPublishedEducationContent = async () => {
  try {
    const cacheKey = "published_education_content";
    const cachedResult = getCachedData(cacheKey);
    if (cachedResult) return cachedResult;

    console.log("🚀 Loading published education content");

    const { data, error } = await supabase.rpc(
      "get_published_education_content"
    );

    if (error) {
      console.error("❌ Database error:", error);
      throw new Error(`Database error: ${error.message}`);
    }

    // Transform data for frontend consumption
    const formattedData = data.map((item) => ({
      id: item.id,
      title: item.title,
      description: item.description,
      content: item.content,
      videoUrl: item.video_url,
      thumbnailUrl: item.thumbnail_url,
      orderIndex: item.order_index,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
    }));

    // Cache the result
    setCachedData(cacheKey, formattedData);

    console.log(`✅ Loaded ${formattedData.length} published education items`);
    return formattedData;
  } catch (err) {
    console.error("❌ Error loading published education content:", err);

    // Return safe fallback data structure
    return [];
  }
};

// Get all education content for admin
export const getAllEducationContent = async () => {
  try {
    const cacheKey = "all_education_content";
    const cachedResult = getCachedData(cacheKey);
    if (cachedResult) return cachedResult;

    console.log("🚀 Loading all education content (admin)");

    const { data, error } = await supabase.rpc("get_all_education_content");

    if (error) {
      console.error("❌ Database error:", error);
      throw new Error(`Database error: ${error.message}`);
    }

    // Transform data for frontend consumption
    const formattedData = data.map((item) => ({
      id: item.id,
      title: item.title,
      description: item.description,
      content: item.content,
      videoUrl: item.video_url,
      thumbnailUrl: item.thumbnail_url,
      orderIndex: item.order_index,
      isPublished: item.is_published,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      createdBy: item.created_by,
    }));

    // Cache the result
    setCachedData(cacheKey, formattedData);

    console.log(`✅ Loaded ${formattedData.length} education items (admin)`);
    return formattedData;
  } catch (err) {
    console.error("❌ Error loading all education content:", err);

    // Return safe fallback data structure
    return [];
  }
};

// Create education content (admin only)
export const createEducationContent = async (contentData) => {
  try {
    console.log("🚀 Creating education content:", contentData.title);

    const { data, error } = await supabase.rpc("create_education_content", {
      p_title: contentData.title,
      p_description: contentData.description || null,
      p_content: contentData.content || null,
      p_video_url: contentData.videoUrl || null,
      p_thumbnail_url: contentData.thumbnailUrl || null,
      p_order_index: contentData.orderIndex || 0,
      p_is_published: contentData.isPublished || false,
    });

    if (error) {
      console.error("❌ Database error:", error);
      throw new Error(`Database error: ${error.message}`);
    }

    // Clear cache to ensure fresh data
    clearEducationCache();

    console.log(`✅ Created education content with ID: ${data}`);
    return { success: true, id: data };
  } catch (err) {
    console.error("❌ Error creating education content:", err);
    return { success: false, error: err.message };
  }
};

// Update education content (admin only)
export const updateEducationContent = async (id, contentData) => {
  try {
    console.log("🚀 Updating education content:", id);

    const { data, error } = await supabase.rpc("update_education_content", {
      p_id: id,
      p_title: contentData.title || null,
      p_description: contentData.description || null,
      p_content: contentData.content || null,
      p_video_url: contentData.videoUrl || null,
      p_thumbnail_url: contentData.thumbnailUrl || null,
      p_order_index: contentData.orderIndex || null,
      p_is_published: contentData.isPublished || null,
    });

    if (error) {
      console.error("❌ Database error:", error);
      throw new Error(`Database error: ${error.message}`);
    }

    if (!data) {
      throw new Error("Content not found or update failed");
    }

    // Clear cache to ensure fresh data
    clearEducationCache();

    console.log(`✅ Updated education content: ${id}`);
    return { success: true };
  } catch (err) {
    console.error("❌ Error updating education content:", err);
    return { success: false, error: err.message };
  }
};

// Delete education content (admin only)
export const deleteEducationContent = async (id) => {
  try {
    console.log("🚀 Deleting education content:", id);

    const { data, error } = await supabase.rpc("delete_education_content", {
      p_id: id,
    });

    if (error) {
      console.error("❌ Database error:", error);
      throw new Error(`Database error: ${error.message}`);
    }

    if (!data) {
      throw new Error("Content not found or delete failed");
    }

    // Clear cache to ensure fresh data
    clearEducationCache();

    console.log(`✅ Deleted education content: ${id}`);
    return { success: true };
  } catch (err) {
    console.error("❌ Error deleting education content:", err);
    return { success: false, error: err.message };
  }
};

// Reorder education content (admin only)
export const reorderEducationContent = async (contentOrders) => {
  try {
    console.log("🚀 Reordering education content");

    const orderData = contentOrders.map((item) => ({
      id: item.id,
      order_index: item.orderIndex,
    }));

    const { error } = await supabase.rpc("reorder_education_content", {
      p_content_orders: JSON.stringify(orderData),
    });

    if (error) {
      console.error("❌ Database error:", error);
      throw new Error(`Database error: ${error.message}`);
    }

    // Clear cache to ensure fresh data
    clearEducationCache();

    console.log(`✅ Reordered ${contentOrders.length} education items`);
    return { success: true };
  } catch (err) {
    console.error("❌ Error reordering education content:", err);
    return { success: false, error: err.message };
  }
};

// Toggle publish status (admin only)
export const togglePublishStatus = async (id, isPublished) => {
  try {
    console.log(
      `🚀 ${isPublished ? "Publishing" : "Unpublishing"} education content:`,
      id
    );

    const result = await updateEducationContent(id, { isPublished });

    if (result.success) {
      console.log(
        `✅ ${isPublished ? "Published" : "Unpublished"} education content: ${id}`
      );
    }

    return result;
  } catch (err) {
    console.error("❌ Error toggling publish status:", err);
    return { success: false, error: err.message };
  }
};

// Helper function to extract YouTube video ID from URL
export const getYouTubeVideoId = (url) => {
  if (!url) return null;

  const regExp =
    /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  return match && match[7].length === 11 ? match[7] : null;
};

// Helper function to get YouTube thumbnail URL
export const getYouTubeThumbnail = (videoId, quality = "hqdefault") => {
  if (!videoId) return null;
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
};

// Helper function to get YouTube embed URL
export const getYouTubeEmbedUrl = (videoId) => {
  if (!videoId) return null;
  return `https://www.youtube.com/embed/${videoId}`;
};

// Upload thumbnail image to Supabase storage
export const uploadThumbnailImage = async (file, contentId = null) => {
  try {
    console.log("🚀 Uploading thumbnail image:", file.name);

    // Generate unique filename
    const fileExt = file.name.split(".").pop();
    const fileName = `${contentId || Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `thumbnails/${fileName}`;

    // Try to upload to profile-photos bucket first, create if doesn't exist
    let bucketName = "profile-photos";
    let { error } = await supabase.storage
      .from(bucketName)
      .upload(filePath, file, {
        cacheControl: "3600",
        upsert: false,
      });

    // If bucket doesn't exist, try to create it
    if (error && error.message.includes("Bucket not found")) {
      console.log("🔧 Creating profile-photos bucket...");

      const { error: bucketError } = await supabase.storage.createBucket(
        bucketName,
        {
          public: true,
          fileSizeLimit: 5242880, // 5MB
          allowedMimeTypes: [
            "image/jpeg",
            "image/png",
            "image/webp",
            "image/gif",
          ],
        }
      );

      if (bucketError) {
        console.error("❌ Bucket creation error:", bucketError);
        throw new Error(`Bucket creation failed: ${bucketError.message}`);
      }

      // Try upload again
      ({ error } = await supabase.storage
        .from(bucketName)
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        }));
    }

    if (error) {
      console.error("❌ Upload error:", error);
      throw new Error(`Upload failed: ${error.message}`);
    }

    // Get public URL
    const {
      data: { publicUrl },
    } = supabase.storage.from(bucketName).getPublicUrl(filePath);

    console.log(`✅ Image uploaded successfully: ${publicUrl}`);

    return {
      success: true,
      url: publicUrl,
      path: filePath,
    };
  } catch (err) {
    console.error("❌ Error uploading thumbnail:", err);
    return {
      success: false,
      error: err.message,
    };
  }
};

// Delete thumbnail image from Supabase storage
export const deleteThumbnailImage = async (filePath) => {
  try {
    console.log("🚀 Deleting thumbnail image:", filePath);

    // Extract path from URL if full URL is provided
    let path = filePath;
    const bucketName = "profile-photos";

    if (filePath.includes(`/storage/v1/object/public/${bucketName}/`)) {
      path = filePath.split(`/storage/v1/object/public/${bucketName}/`)[1];
    }

    const { error } = await supabase.storage.from(bucketName).remove([path]);

    if (error) {
      console.error("❌ Delete error:", error);
      throw new Error(`Delete failed: ${error.message}`);
    }

    console.log(`✅ Image deleted successfully: ${path}`);
    return { success: true };
  } catch (err) {
    console.error("❌ Error deleting thumbnail:", err);
    return {
      success: false,
      error: err.message,
    };
  }
};
